import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "@tanstack/react-router";
import {
  Check,
  ChevronsUpDown,
  CreditCard,
  FolderKanban,
  Globe2,
  LoaderCircle,
  LogOut,
  Menu,
  UserRound,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { requestWorkspaceLogout } from "@/workspace/session";

import {
  AccountApiError,
  fetchAccount,
  fetchAccounts,
  switchAccount,
  type AccountOption,
  type AccountProfile,
} from "./api";

type SignedInArea = "projects" | "domains" | "billing" | "account";

const AccountContext = createContext<AccountProfile | null>(null);

export function useSignedInAccount(): AccountProfile {
  const account = useContext(AccountContext);
  if (!account) throw new Error("Signed-in account is unavailable");
  return account;
}

export function SignedInShell({
  active,
  mode = "page",
  children,
}: {
  active: SignedInArea;
  mode?: "page" | "editor";
  children: ReactNode;
}): ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const accountQuery = useQuery({
    queryKey: ["signed-in-account"],
    queryFn: fetchAccount,
    retry: false,
  });
  const accountsQuery = useQuery({
    queryKey: ["signed-in-accounts"],
    queryFn: fetchAccounts,
    retry: false,
  });
  const logout = useMutation({ mutationFn: requestWorkspaceLogout });
  const accountSwitch = useMutation({
    mutationFn: switchAccount,
    onSuccess: async (account) => {
      queryClient.setQueryData(["signed-in-account"], account);
      await queryClient.invalidateQueries();
      await navigate({ to: "/workspaces" });
    },
  });

  useEffect(() => {
    if (!menuOpen) return;
    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  if (accountQuery.isPending) return <SignedInLoading />;
  if (
    accountQuery.error instanceof AccountApiError &&
    accountQuery.error.status === 401
  ) {
    return <Navigate to="/login" replace />;
  }
  if (!accountQuery.data) {
    return (
      <div className="signed-in-sky flex min-h-[100dvh] items-center justify-center px-6 text-[#20272b]">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold">Couldn't load your account</h1>
          <p className="mt-2 text-sm leading-6 text-[#6f7773]">
            {accountQuery.error?.message ?? "Please try again."}
          </p>
          <Button
            type="button"
            className="mt-5 rounded-full bg-[#dceffc] text-[#20272b] shadow-none hover:bg-[#c8e6fa]"
            onClick={() => void accountQuery.refetch()}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }
  if (active === "billing" && accountQuery.data.role !== "owner") {
    return <Navigate to="/workspaces" replace />;
  }

  async function signOut(): Promise<void> {
    const logoutUrl = await logout.mutateAsync();
    window.location.href = logoutUrl ?? "/login";
  }

  return (
    <AccountContext.Provider value={accountQuery.data}>
      <div className="signed-in-sky flex h-[100dvh] min-h-0 text-[#20272b] lg:grid lg:grid-cols-[13.5rem_minmax(0,1fr)]">
        <Sidebar
          account={accountQuery.data}
          accounts={accountsQuery.data ?? []}
          active={active}
          signingOut={logout.isPending}
          switchingAccountId={
            accountSwitch.isPending ? accountSwitch.variables : undefined
          }
          switchError={accountSwitch.error?.message}
          onSwitchAccount={async (accountId) => {
            await accountSwitch.mutateAsync(accountId);
          }}
          onSignOut={signOut}
        />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <MobileHeader
            active={active}
            open={menuOpen}
            onOpen={() => setMenuOpen(true)}
          />
          <div
            className={cn(
              "min-h-0 flex-1",
              mode === "editor" ? "overflow-hidden" : "overflow-y-auto",
            )}
          >
            {children}
          </div>
        </div>

        {menuOpen ? (
          <MobileMenu
            account={accountQuery.data}
            accounts={accountsQuery.data ?? []}
            active={active}
            signingOut={logout.isPending}
            switchingAccountId={
              accountSwitch.isPending ? accountSwitch.variables : undefined
            }
            switchError={accountSwitch.error?.message}
            onClose={() => setMenuOpen(false)}
            onSwitchAccount={async (accountId) => {
              await accountSwitch.mutateAsync(accountId);
            }}
            onSignOut={signOut}
          />
        ) : null}
      </div>
    </AccountContext.Provider>
  );
}

const navigation = [
  { id: "projects", label: "Projects", href: "/workspaces", icon: FolderKanban },
  { id: "domains", label: "Domains", href: "/domains", icon: Globe2 },
  { id: "billing", label: "Billing", href: "/billing", icon: CreditCard },
] as const;

function navigationForRole(role: AccountProfile["role"]) {
  return role === "owner"
    ? navigation
    : navigation.filter((item) => item.id !== "billing");
}

function Sidebar({
  account,
  accounts,
  active,
  signingOut,
  switchingAccountId,
  switchError,
  onSwitchAccount,
  onSignOut,
}: {
  account: AccountProfile;
  accounts: AccountOption[];
  active: SignedInArea;
  signingOut: boolean;
  switchingAccountId?: string;
  switchError?: string;
  onSwitchAccount: (accountId: string) => Promise<void>;
  onSignOut: () => Promise<void>;
}): ReactElement {
  return (
    <aside className="hidden min-h-0 flex-col border-r border-[#d2e8f6] bg-white/65 px-3 py-4 backdrop-blur-xl lg:flex">
      <a
        href="/workspaces"
        className="flex items-center gap-2.5 rounded-xl px-2 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]"
        aria-label="Chill projects"
      >
        <ChillMark />
          <span className="font-['Avenir_Next',Inter,sans-serif] text-base font-semibold tracking-[-0.04em]">
          chill
        </span>
      </a>

      <nav className="mt-7 space-y-1" aria-label="Account navigation">
        {navigationForRole(account.role).map((item) => (
          <NavigationLink key={item.id} item={item} active={active === item.id} />
        ))}
      </nav>

      <div className="mt-auto border-t border-[#d2e8f6] pt-3">
        <AccountSwitcher
          account={account}
          accounts={accounts}
          activeArea={active}
          switchingAccountId={switchingAccountId}
          error={switchError}
          onSelect={onSwitchAccount}
        />
        <button
          type="button"
          disabled={signingOut}
          onClick={() => void onSignOut()}
          className="mt-1 flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm text-[#52606e] hover:bg-white/75 hover:text-[#20272b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8] disabled:opacity-50"
        >
          <LogOut className="size-4" aria-hidden="true" />
          {signingOut ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </aside>
  );
}

function AccountSwitcher({
  account,
  accounts,
  activeArea,
  switchingAccountId,
  error,
  onSelect,
}: {
  account: AccountProfile;
  accounts: AccountOption[];
  activeArea: SignedInArea;
  switchingAccountId?: string;
  error?: string;
  onSelect: (accountId: string) => Promise<void>;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const choices = accounts.length > 0
    ? accounts
    : [{ id: "", name: account.name, role: account.role, active: true }];

  useEffect(() => {
    if (!open) return;
    function closeOutside(event: MouseEvent): void {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      {open ? (
        <div
          role="menu"
          aria-label="Accounts"
          className="absolute bottom-full left-0 z-50 mb-2 w-[min(19rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-[#c9dfea] bg-[#fbfdff] p-2 shadow-[0_18px_48px_rgba(74,111,139,0.18)]"
        >
          <div className="px-2 pb-1.5 pt-1">
            <p className="text-xs font-medium text-[#52606e]">Accounts</p>
            <p className="truncate text-[11px] text-[#8a928f]">{account.email}</p>
          </div>
          <div className="space-y-1">
            {choices.map((choice) => {
              const switching = switchingAccountId === choice.id;
              return (
                <button
                  key={choice.id || choice.name}
                  type="button"
                  role="menuitemradio"
                  aria-label={`${choice.name}, ${choice.role}${choice.active ? ", current" : ""}`}
                  aria-checked={choice.active}
                  disabled={choice.active || !choice.id || Boolean(switchingAccountId)}
                  onClick={() => {
                    void onSelect(choice.id).catch(() => undefined);
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8] disabled:cursor-default",
                    choice.active
                      ? "bg-[#dceffc]/80"
                      : "hover:bg-[#eef7fd] disabled:opacity-55",
                  )}
                >
                  <AccountInitial name={choice.name} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {choice.name}
                    </span>
                    <span className="block text-xs capitalize text-[#747d79]">
                      {switching ? "Switching…" : choice.role}
                    </span>
                  </span>
                  {choice.active ? (
                    <Check className="size-4 text-[#4f527c]" aria-hidden="true" />
                  ) : null}
                </button>
              );
            })}
          </div>
          {error ? (
            <p className="px-2.5 py-2 text-xs text-[#9b3f45]">{error}</p>
          ) : null}
          <div className="mt-2 border-t border-[#d9e9f3] pt-2">
            <a
              href="/account"
              aria-current={activeArea === "account" ? "page" : undefined}
              className="flex items-center gap-2 rounded-xl px-2.5 py-2 text-sm text-[#52606e] hover:bg-[#eef7fd] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]"
            >
              <UserRound className="size-4" aria-hidden="true" />
              Account settings
            </a>
          </div>
        </div>
      ) : null}
      <button
        type="button"
        aria-label={`Switch account, current account ${account.name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          "flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]",
          activeArea === "account" ? "bg-[#dceffc]/85" : "hover:bg-white/75",
        )}
      >
        <AccountInitial name={account.name} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{account.name}</span>
          <span className="block truncate text-[11px] text-[#747d79]">
            {account.email}
          </span>
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-[#747d79]" aria-hidden="true" />
      </button>
    </div>
  );
}

function AccountInitial({ name }: { name: string }): ReactElement {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#e5e4f4] text-xs font-semibold text-[#4f527c]">
      {name.trim().charAt(0).toUpperCase() || "C"}
    </span>
  );
}

function NavigationLink({
  item,
  active,
}: {
  item: (typeof navigation)[number];
  active: boolean;
}): ReactElement {
  const Icon = item.icon;
  return (
    <a
      href={item.href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]",
        active
          ? "bg-[#dceffc]/85 font-medium text-[#20272b]"
          : "text-[#52606e] hover:bg-white/75 hover:text-[#20272b]",
      )}
    >
      <Icon className="size-4" aria-hidden="true" />
      {item.label}
    </a>
  );
}

function MobileHeader({
  active,
  open,
  onOpen,
}: {
  active: SignedInArea;
  open: boolean;
  onOpen: () => void;
}): ReactElement {
  const label =
    active === "projects"
      ? "Projects"
      : active.charAt(0).toUpperCase() + active.slice(1);
  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-[#d2e8f6] bg-white/70 px-4 backdrop-blur-xl lg:hidden">
      <a href="/workspaces" className="flex items-center gap-2" aria-label="Chill projects">
        <ChillMark />
        <span className="font-['Avenir_Next',Inter,sans-serif] text-base font-semibold tracking-[-0.04em]">chill</span>
        <span className="ml-1 border-l border-[#c9dfea] pl-3 text-sm text-[#52606e]">{label}</span>
      </a>
      <button
        type="button"
        onClick={onOpen}
        aria-label="Open navigation"
        aria-expanded={open}
        className="flex size-9 items-center justify-center rounded-full hover:bg-[#dceffc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]"
      >
        <Menu className="size-5" aria-hidden="true" />
      </button>
    </header>
  );
}

function MobileMenu({
  account,
  accounts,
  active,
  signingOut,
  switchingAccountId,
  switchError,
  onClose,
  onSwitchAccount,
  onSignOut,
}: {
  account: AccountProfile;
  accounts: AccountOption[];
  active: SignedInArea;
  signingOut: boolean;
  switchingAccountId?: string;
  switchError?: string;
  onClose: () => void;
  onSwitchAccount: (accountId: string) => Promise<void>;
  onSignOut: () => Promise<void>;
}): ReactElement {
  return (
    <div className="fixed inset-0 z-[80] bg-[#20272b]/20 backdrop-blur-[2px] lg:hidden" role="presentation" onClick={onClose}>
      <aside
        className="ml-auto flex h-full w-[min(20rem,88vw)] flex-col border-l border-[#d2e8f6] bg-[#f5fbff] p-4 shadow-[-20px_0_50px_rgba(80,129,164,0.16)]"
        aria-label="Account menu"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm font-semibold">
            <ChillMark /> chill
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="flex size-9 items-center justify-center rounded-xl hover:bg-white"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
        <nav className="mt-8 space-y-1" aria-label="Account navigation">
          {navigationForRole(account.role).map((item) => (
            <NavigationLink key={item.id} item={item} active={active === item.id} />
          ))}
          <a
            href="/account"
            aria-current={active === "account" ? "page" : undefined}
            className={cn(
              "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm",
              active === "account"
                ? "bg-[#dceffc] font-medium text-[#20272b]"
                : "text-[#52606e] hover:bg-white/75",
            )}
          >
            <UserRound className="size-4" aria-hidden="true" /> Account
          </a>
        </nav>
        <div className="mt-auto border-t border-[#d2e8f6] pt-4">
          <AccountSwitcher
            account={account}
            accounts={accounts}
            activeArea={active}
            switchingAccountId={switchingAccountId}
            error={switchError}
            onSelect={onSwitchAccount}
          />
          <p className="mt-2 truncate px-2 text-xs text-[#747d79]">
            {account.email}
          </p>
          <button
            type="button"
            disabled={signingOut}
            onClick={() => void onSignOut()}
            className="mt-4 flex items-center gap-2 px-2 text-sm text-[#52606e] disabled:opacity-50"
          >
            <LogOut className="size-4" aria-hidden="true" />
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      </aside>
    </div>
  );
}

function ChillMark(): ReactElement {
  return (
    <img
      src="/brand/chill-icon-upload.png"
      alt=""
      className="size-8 shrink-0 object-contain"
    />
  );
}

function SignedInLoading(): ReactElement {
  return (
    <div className="signed-in-sky flex min-h-[100dvh] items-center justify-center text-[#52606e]">
      <LoaderCircle className="size-5 animate-spin" aria-label="Loading account" />
    </div>
  );
}
